/**
 * UnconfirmedCandidatesModal — surfaces BEP candidate rules that were
 * extracted but never activated, and lets the user pick more to add
 * without re-running BEP extraction.
 *
 * Data source: ``GET /api/projects/{project_id}/bep/unconfirmed-candidates``.
 * Activate action: ``POST /api/projects/{project_id}/bep/activate-candidates``
 * with a body of ``{rule_ids: [...]}``.
 *
 * The modal is conditionally rendered by the parent — it only shows
 * when ``open`` is true. The parent passes the same ``unconfirmed_count``
 * it computed for the trigger button so we don't double-fetch on open.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, ListPlus, Square, CheckSquare } from 'lucide-react';
import { apiFetch } from '../lib/supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mirrors SEVERITY_COLORS in ProjectDetailPage.js so the modal's badge
// palette matches the rest of the rule UI. Imported as constants
// rather than via an export to keep the modal self-contained.
const SEVERITY_BADGE = {
  critical: 'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  high:     'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  major:    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  medium:   'bg-accent/10 text-accent border-accent/30',
  minor:    'bg-muted/40 text-muted-foreground border-muted',
};

const truncate = (s, n) => {
  if (!s) return '';
  const str = String(s);
  return str.length > n ? str.slice(0, n - 1) + '…' : str;
};

export default function UnconfirmedCandidatesModal({
  open,
  onOpenChange,
  projectId,
  onActivated,
}) {
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);

  const fetchCandidates = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/bep/unconfirmed-candidates`
      );
      if (!resp.ok) throw new Error(`Failed (${resp.status})`);
      const json = await resp.json();
      setCandidates(json.candidates || []);
      setTotalCandidates(json.total_candidates || 0);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error('Could not load unconfirmed candidates', { description: e.message || '' });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) fetchCandidates();
  }, [open, fetchCandidates]);

  const toggleId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(candidates.map(c => c.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const activate = async () => {
    if (selectedIds.size === 0) return;
    setActivating(true);
    try {
      const resp = await apiFetch(
        `${API_URL}/api/projects/${projectId}/bep/activate-candidates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rule_ids: Array.from(selectedIds) }),
        }
      );
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Failed (${resp.status}): ${t.slice(0, 160)}`);
      }
      const json = await resp.json();
      toast.success(
        `Activated ${json.activated} rule${json.activated === 1 ? '' : 's'}`,
        json.skipped_already_confirmed?.length
          ? { description: `${json.skipped_already_confirmed.length} were already confirmed and skipped.` }
          : undefined,
      );
      onOpenChange(false);
      if (typeof onActivated === 'function') onActivated();
    } catch (e) {
      toast.error('Activation failed', { description: e.message || '' });
    } finally {
      setActivating(false);
    }
  };

  const allSelected = useMemo(
    () => candidates.length > 0 && selectedIds.size === candidates.length,
    [candidates, selectedIds]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="unconfirmed-candidates-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="h-4 w-4 text-accent" />
            Available rules from last extraction
          </DialogTitle>
          <DialogDescription>
            {totalCandidates} extracted · {candidates.length} not yet activated. Select the rules you want to add to the active validation set — no BEP re-extraction needed.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2 border-b border-border">
          <Button
            size="sm" variant="ghost"
            onClick={allSelected ? deselectAll : selectAll}
            disabled={loading || candidates.length === 0}
            data-testid="select-all-candidates-btn"
          >
            {allSelected
              ? <><Square className="h-3.5 w-3.5 mr-1" /> Deselect all</>
              : <><CheckSquare className="h-3.5 w-3.5 mr-1" /> Select all</>}
          </Button>
          <span className="text-xs text-muted-foreground ml-auto">
            {selectedIds.size} of {candidates.length} selected
          </span>
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1.5"
             data-testid="unconfirmed-candidates-list">
          {loading && (
            <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading candidates…
            </div>
          )}
          {!loading && candidates.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              All extracted candidates are already activated.
            </p>
          )}
          {!loading && candidates.map((c, idx) => {
            const id = c.id;
            const isChecked = selectedIds.has(id);
            const sevClass = SEVERITY_BADGE[(c.severity || '').toLowerCase()] || SEVERITY_BADGE.major;
            return (
              <label
                key={id}
                className={`flex items-start gap-2 p-2.5 rounded border cursor-pointer transition-colors ${
                  isChecked ? 'border-accent/40 bg-accent/5' : 'border-border hover:bg-muted/30'
                }`}
                data-testid={`candidate-row-${id}`}
              >
                {/* 1-based index — frontend reference only */}
                <span
                  className="mt-0.5 inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1.5 rounded-md text-[10px] font-semibold tabular-nums bg-muted text-muted-foreground shrink-0"
                  data-testid={`candidate-index-${idx + 1}`}
                  title={`Rule #${idx + 1} of ${candidates.length}`}
                >
                  #{idx + 1}
                </span>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleId(id)}
                  className="mt-1 accent-[hsl(var(--accent))]"
                  data-testid={`candidate-checkbox-${id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <Badge variant="outline" className="text-[10px] px-1.5">
                      {c.category || 'GENERAL'}
                    </Badge>
                    <Badge className={`text-[10px] px-1.5 border ${sevClass}`}>
                      {c.severity || 'major'}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground font-mono">
                      {id}
                    </span>
                  </div>
                  <p className="text-sm text-foreground">{c.description || '(no description)'}</p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-1">
                    check: {c.check_type || '—'}
                    {c.field_to_check && ` · field: ${c.field_to_check}`}
                    {c.pset_name && ` · pset: ${c.pset_name}`}
                    {c.expected_value !== undefined && c.expected_value !== '' &&
                      ` · expected: ${truncate(typeof c.expected_value === 'string' ? c.expected_value : JSON.stringify(c.expected_value), 40)}`}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="cancel-candidates-btn">
            Cancel
          </Button>
          <Button
            onClick={activate}
            disabled={activating || selectedIds.size === 0}
            data-testid="activate-candidates-btn"
            className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90 text-white"
          >
            {activating
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Activating…</>
              : <>Activate selected ({selectedIds.size})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
